import {
  EXPORT_FIELD,
  EXPORT_FORMAT,
  EXPORT_LIMITS,
  EXPORT_SECTION,
  EXPORT_SECTION_ORDER,
  EXPORT_VERSION,
  META_FIELD,
} from './catalog';
import type { ExportSection } from './catalog';
import type { CsvRow } from './csv';
import { EXPORT_ISSUE, issueFor } from './issue';
import { fail, ok } from './result';
import type { ExportResult } from './result';

/**
 * A parsed export file: the rows of each section, keyed by field name.
 *
 * The file is read in two steps on purpose. This module answers "is this our
 * file, and does it have the sections and columns we know?" — nothing about
 * loads or keypads. `rcu.ts` and `keypads.ts` then answer "do these rows mean
 * anything?" by handing the reconstructed shapes to the decoders that already
 * guard storage. Neither step can do the other's job, which is what keeps a
 * structural mistake from being reported as a bad record.
 */

/** One data row, already checked against its section's header. */
export type DocumentRow = Readonly<Record<string, string>>;

export interface ExportDocument {
  readonly format: string;
  readonly version: number;
  readonly exportedAt: string;
  /** Every section, whether or not the file carried it. */
  readonly rows: Readonly<Record<ExportSection, readonly DocumentRow[]>>;
}

/** The section a row belongs to, or null when its first cell names no section. */
const sectionOf = (row: CsvRow): ExportSection | null => {
  const name = row[0] ?? '';
  return EXPORT_SECTION_ORDER.find((section) => section === name) ?? null;
};

/** True when this row is the header of its section rather than a data row. */
const isHeader = (row: CsvRow, section: ExportSection): boolean => {
  const fields = EXPORT_FIELD[section];
  if (row.length !== fields.length + 1) return false;
  return fields.every((field, index) => row[index + 1] === field);
};

/** The row as fields keyed by name, padded when trailing cells were omitted. */
const keyedRow = (row: CsvRow, section: ExportSection): DocumentRow => {
  const fields = EXPORT_FIELD[section];
  const entries = fields.map((field, index) => [field, row[index + 1] ?? ''] as const);
  return Object.fromEntries(entries);
};

/**
 * Turns parsed CSV rows into a document.
 *
 * A row may omit its trailing empty cells — a hand-edited file where only the
 * first three columns of a load are filled in should not have to spell out seven
 * commas — but it may never carry *more* cells than its header, because that is
 * the one mistake that would silently shift every value one column to the left.
 *
 * A section may appear more than once; its rows accumulate. A section that is
 * absent is empty rather than an error, because the file describes the whole
 * tablet and a tablet with no DALI bridges writes a header and nothing else.
 */
export const rowsToDocument = (rows: readonly CsvRow[]): ExportResult<ExportDocument> => {
  if (rows.length === 0) return fail(issueFor(EXPORT_ISSUE.EMPTY_FILE));

  const buckets = new Map<ExportSection, DocumentRow[]>();
  const seenHeaders = new Set<ExportSection>();

  for (const [index, row] of rows.entries()) {
    const line = index + 1;
    const section = sectionOf(row);

    if (section === null) {
      const name = row[0] ?? '';
      return fail(
        issueFor(
          EXPORT_ISSUE.UNKNOWN_SECTION,
          name === '' ? `a row on line ${line} names no section` : `“${name}”, on line ${line}`,
        ),
      );
    }

    if (isHeader(row, section)) {
      seenHeaders.add(section);
      if (!buckets.has(section)) buckets.set(section, []);
      continue;
    }

    if (!seenHeaders.has(section)) {
      return fail(
        issueFor(EXPORT_ISSUE.SECTION_MISMATCH, `${section}, whose data starts on line ${line}`),
      );
    }

    const fields = EXPORT_FIELD[section];
    if (row.length > fields.length + 1) {
      return fail(
        issueFor(
          EXPORT_ISSUE.ROW_SHAPE,
          `line ${line} has ${row.length - 1} fields where ${section} takes ${fields.length}`,
        ),
      );
    }

    const bucket = buckets.get(section) ?? [];
    if (bucket.length >= EXPORT_LIMITS.MAX_SECTION_ROWS) {
      return fail(issueFor(EXPORT_ISSUE.TOO_MANY_ROWS, section));
    }
    bucket.push(keyedRow(row, section));
    buckets.set(section, bucket);
  }

  const meta = buckets.get(EXPORT_SECTION.META)?.[0];
  if (meta === undefined || meta[META_FIELD.FORMAT] !== EXPORT_FORMAT) {
    return fail(issueFor(EXPORT_ISSUE.UNKNOWN_FORMAT));
  }

  const version = Number(meta[META_FIELD.VERSION] ?? '');
  if (!Number.isInteger(version)) return fail(issueFor(EXPORT_ISSUE.UNKNOWN_FORMAT));
  if (version !== EXPORT_VERSION) {
    return fail(issueFor(EXPORT_ISSUE.VERSION_UNSUPPORTED, String(version)));
  }

  return ok({
    format: EXPORT_FORMAT,
    version,
    exportedAt: meta[META_FIELD.EXPORTED_AT] ?? '',
    rows: Object.fromEntries(
      EXPORT_SECTION_ORDER.map((section) => [section, buckets.get(section) ?? []]),
    ) as unknown as Readonly<Record<ExportSection, readonly DocumentRow[]>>,
  });
};

/**
 * The rows of one section, or none when the file did not carry it.
 *
 * Defensive about its input as well as its output: this is the boundary the two
 * decoders read through, and a caller that hands it a document it built by hand
 * should get an empty section rather than a crash halfway through a panel.
 */
export const rowsOf = (document: ExportDocument, section: ExportSection): readonly DocumentRow[] => {
  const rows: unknown = document.rows[section];
  return Array.isArray(rows) ? (rows as readonly DocumentRow[]) : [];
};

/** The single row of a section that holds at most one, or null. */
export const singleRowOf = (document: ExportDocument, section: ExportSection): DocumentRow | null =>
  document.rows[section][0] ?? null;
